import { useState, useCallback, useRef, useEffect } from "react";
import { format } from "date-fns";
import AppConstants from "../../../Constants/AppConstants";
import {
  validateAllSearchFields,
  hasSearchCriteria,
} from "../utils/validators";

/**
 * Custom hook for personal member search functionality
 */
export const usePersonalSearch = (membersTab, doAccountSearch) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });
  const [searchError, setSearchError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [homeError, setHomeError] = useState("");
  const [sin4Error, setSin4Error] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const isInitialRender = useRef(true);
  const lastSearchParamsRef = useRef(null);

  const [sortConfig, setSortConfig] = useState({
    sortBy: "created",
    sortDirection: "DESC",
  });

  // Sort handler function
  const handleSort = (field) => {
    const newDirection =
      sortConfig.sortBy === field && sortConfig.sortDirection === "ASC"
        ? "DESC"
        : "ASC";

    setSortConfig({
      sortBy: field,
      sortDirection: newDirection,
    });

    setPagination((prev) => ({ ...prev, currentPage: 0 }));
  };

  // Execute search function
  const executePersonalSearch = useCallback(
    (searchParams) => {
      if (membersTab !== "personal") return;

      if (!hasSearchCriteria(searchParams)) {
        setSearchError("At least one field is required to initiate a search.");
        return;
      } else {
        setSearchError("");
        setSearchResults([]);
        setIsLoading(true);
        setHasSearched(true);
      }

      lastSearchParamsRef.current = searchParams;

      const searchCriteria = {
        firstName: searchParams.firstName || "",
        middleName: searchParams.middleName || "",
        lastName: searchParams.lastName || "",
        dateOfBirth: searchParams.dob
          ? format(new Date(searchParams.dob), "yyyy-MM-dd")
          : null,
        sin: searchParams.sin4 || "",
        pan: searchParams.pan || "",
        memberNumber: searchParams.memberNumber || "",
        mobileNumber: searchParams.mobile || "",
        homeNumber: searchParams.home || "",
        email: searchParams.email || "",
        address: searchParams.address || "",
        city: searchParams.city || "",
        province: searchParams.state || "",
        postalCode: searchParams.zip || "",
        country: searchParams.country || "",
        bankingType: AppConstants.BANKING_TYPE.PERSONAL,
        page: pagination.currentPage,
        size: pagination.pageSize,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      };

      doAccountSearch(searchCriteria, (response) => {
        setIsLoading(false);

        if (response.error) {
          console.error("Search failed:", response.error);
          setSearchResults([]);
          setPagination({
            currentPage: 0,
            pageSize: 20,
            totalElements: 0,
            totalPages: 0,
            hasNext: false,
            hasPrevious: false,
          });
        } else {
          const responseData = response.data || response;
          setSearchResults(responseData?.results || []);
          setPagination({
            currentPage: responseData?.currentPage || 0,
            pageSize: responseData?.pageSize || 20,
            totalElements: responseData?.totalElements || 0,
            totalPages: responseData?.totalPages || 0,
            hasNext: responseData?.hasNext || false,
            hasPrevious: responseData?.hasPrevious || false,
          });
        }
      });
    },
    [
      membersTab,
      pagination.currentPage,
      pagination.pageSize,
      sortConfig.sortBy,
      sortConfig.sortDirection,
      doAccountSearch,
    ]
  );

  // Function for explicit search button clicks
  const handlePersonalSearch = useCallback(
    (search) => {
      // Clear all validation errors
      setEmailError("");
      setMobileError("");
      setHomeError("");
      setSin4Error("");

      const validation = validateAllSearchFields(search);

      if (!validation.isValid) {
        if (validation.errors.email) setEmailError(validation.errors.email);
        if (validation.errors.mobile) setMobileError(validation.errors.mobile);
        if (validation.errors.home) setHomeError(validation.errors.home);
        if (validation.errors.sin4) setSin4Error(validation.errors.sin4);
        return;
      }

      executePersonalSearch(search);
    },
    [executePersonalSearch]
  );

  // Function for pagination/sorting - uses stored search parameters
  const handlePersonalSearchWithStoredParams = useCallback(() => {
    if (lastSearchParamsRef.current) {
      executePersonalSearch(lastSearchParamsRef.current);
    }
  }, [executePersonalSearch]);

  // Execute search when pagination or sorting changes
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (hasSearched && membersTab === "personal") {
      handlePersonalSearchWithStoredParams();
    }
  }, [
    membersTab,
    pagination.currentPage,
    pagination.pageSize,
    sortConfig.sortBy,
    sortConfig.sortDirection,
    hasSearched,
    handlePersonalSearchWithStoredParams,
  ]);

  // Reset function
  const resetSearch = useCallback(() => {
    setSearchResults([]);
    setHasSearched(false);
    setIsLoading(false);
    setSearchError("");
    setEmailError("");
    setMobileError("");
    setHomeError("");
    setSin4Error("");
    setPagination({
      currentPage: 0,
      pageSize: 20,
      totalElements: 0,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
    });
    lastSearchParamsRef.current = null;
  }, []);

  return {
    searchResults,
    isLoading,
    pagination,
    setPagination,
    searchError,
    emailError,
    setEmailError,
    mobileError,
    setMobileError,
    homeError,
    setHomeError,
    sin4Error,
    setSin4Error,
    hasSearched,
    sortConfig,
    handleSort,
    handlePersonalSearch,
    handlePersonalSearchWithStoredParams,
    resetSearch,
  };
};
